/**
 * Real geometry for the 3D figures.
 *
 * The 3D objects used to be flat SVG drawings with the highlights and shadows painted on by
 * hand, which meant they could only ever be spun in the plane of the page -- there was no
 * "other side" to turn towards. Here each one is a genuine mesh instead: vertices and faces,
 * turned by three angles, projected, and lit, so tilting one really does show a different face
 * and the shading follows the light instead of staying stuck to the picture.
 *
 * Coordinates: meshes are built in a unit box centred on the origin, x/y/z each -0.5..0.5,
 * with Y pointing UP and Z towards the viewer (the ordinary right-handed maths convention).
 * The Y flip into screen coordinates happens once, at the very end of project3D.
 *
 * Both consumers -- the live SVG layer and the canvas exporter used by save / share / print --
 * call project3D, so what is printed is what was on screen.
 */

import { shade } from './shapeLibrary';

const TAU = Math.PI * 2;

/* --------------------------------- meshes --------------------------------- */
/* Each builder returns { verts: [[x,y,z], ...], faces: [[i, j, k, ...], ...] } with every
   face wound counter-clockwise as seen from OUTSIDE the solid, which is what lets a face be
   dropped when it turns away from the viewer. */

// A horizontal ring of points. z runs NEGATIVE with the angle so that, looked at from above,
// the points go counter-clockwise: that is what makes a cap built straight from this ring face
// upwards, and the side walls between two rings face outwards, without any further flipping.
const ring = (verts, cy, r, seg, rz = null) => {
  const idx = [];
  for (let i = 0; i < seg; i++) {
    const a = (i / seg) * TAU;
    idx.push(verts.length);
    verts.push([Math.cos(a) * r, cy, -(rz == null ? Math.sin(a) * r : Math.sin(a) * rz)]);
  }
  return idx;
};

// Join two rings of equal length into a band of quads. `flip` reverses the winding for the
// case where the second ring is below the first rather than above it.
const band = (faces, lower, upper, flip = false) => {
  const n = lower.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    faces.push(flip
      ? [lower[i], upper[i], upper[j], lower[j]]
      : [lower[i], lower[j], upper[j], upper[i]]);
  }
};

const cubeMesh = () => ({
  verts: [
    [-0.5, -0.5,  0.5], [0.5, -0.5,  0.5], [0.5, 0.5,  0.5], [-0.5, 0.5,  0.5],  // front
    [-0.5, -0.5, -0.5], [0.5, -0.5, -0.5], [0.5, 0.5, -0.5], [-0.5, 0.5, -0.5],  // back
  ],
  faces: [
    [0, 1, 2, 3],   // front
    [5, 4, 7, 6],   // back
    [1, 5, 6, 2],   // right
    [4, 0, 3, 7],   // left
    [3, 2, 6, 7],   // top
    [4, 5, 1, 0],   // bottom
  ],
});

const pyramidMesh = () => ({
  verts: [
    [-0.5, -0.5,  0.5], [0.5, -0.5,  0.5], [0.5, -0.5, -0.5], [-0.5, -0.5, -0.5],
    [0, 0.5, 0],
  ],
  faces: [
    [0, 1, 4], [1, 2, 4], [2, 3, 4], [3, 0, 4],
    [3, 2, 1, 0],   // base, seen from below
  ],
});

const cylinderMesh = (seg = 28) => {
  const verts = [];
  const bottom = ring(verts, -0.5, 0.5, seg);
  const top = ring(verts, 0.5, 0.5, seg);
  const faces = [];
  band(faces, bottom, top);
  faces.push(top.slice());
  faces.push(bottom.slice().reverse());
  return { verts, faces };
};

const coneMesh = (seg = 28) => {
  const verts = [];
  const base = ring(verts, -0.5, 0.5, seg);
  const apex = verts.length;
  verts.push([0, 0.5, 0]);
  const faces = [];
  for (let i = 0; i < seg; i++) faces.push([base[i], base[(i + 1) % seg], apex]);
  faces.push(base.slice().reverse());
  return { verts, faces };
};

const hemisphereMesh = (slices = 30, stacks = 12) => {
  const verts = [], faces = [];
  // The dome fills the box: flat face at the bottom, top of the dome at the top.
  const rings = [];
  for (let s = 0; s <= stacks; s++) {
    const t = s / stacks;                 // 0 at the rim, 1 at the pole
    const a = (t * Math.PI) / 2;
    rings.push(ring(verts, -0.5 + Math.sin(a), Math.cos(a) * 0.5, slices));
  }
  for (let s = 0; s < stacks; s++) band(faces, rings[s], rings[s + 1]);
  faces.push(rings[0].slice().reverse());   // the flat underside
  return { verts, faces };
};

const capsuleMesh = (slices = 20, capStacks = 6) => {
  const verts = [], faces = [];
  const r = 0.28;                       // tube radius; the caps take r off each end
  const yBody = 0.5 - r;
  const rings = [];
  for (let s = 0; s <= capStacks; s++) {           // bottom cap, pole upward
    const a = (Math.PI / 2) * (1 - s / capStacks);
    rings.push(ring(verts, -yBody - Math.sin(a) * r, Math.cos(a) * r, slices));
  }
  for (let s = 0; s <= capStacks; s++) {           // top cap
    const a = (Math.PI / 2) * (s / capStacks);
    rings.push(ring(verts, yBody + Math.sin(a) * r, Math.cos(a) * r, slices));
  }
  for (let s = 0; s < rings.length - 1; s++) band(faces, rings[s], rings[s + 1]);
  return { verts, faces };
};

const torusMesh = (major = 26, minor = 11) => {
  const R = 0.36, r = 0.13;
  const verts = [], faces = [];
  const rings = [];
  for (let i = 0; i < major; i++) {
    const a = (i / major) * TAU;
    const ca = Math.cos(a), sa = Math.sin(a);
    const idx = [];
    for (let j = 0; j < minor; j++) {
      const b = (j / minor) * TAU;
      const rr = R + Math.cos(b) * r;
      idx.push(verts.length);
      // Ring lying in the x/y plane, so face-on it reads as the ring it always was.
      verts.push([ca * rr, sa * rr, Math.sin(b) * r]);
    }
    rings.push(idx);
  }
  // Arguments the other way round: swapping them reverses each quad's winding, which is what
  // turns this tube's faces outward rather than inward.
  for (let i = 0; i < major; i++) band(faces, rings[(i + 1) % major], rings[i]);
  return { verts, faces };
};

// An elbow: a circular tube swept along a quarter turn, with both mouths capped.
const pipeMesh = (along = 18, around = 12) => {
  const R = 0.62, r = 0.15;
  const verts = [], faces = [];
  const rings = [];
  for (let i = 0; i <= along; i++) {
    const a = (Math.PI / 2) * (i / along);          // 0 -> vertical arm, PI/2 -> horizontal
    const cx = -0.5 + R * Math.sin(a);
    const cy = 0.5 - R * (1 - Math.cos(a));
    // Frame: tangent along the arc, normal in-plane, binormal out of the plane.
    const nx = Math.sin(a), ny = Math.cos(a);       // in-plane normal
    const idx = [];
    for (let j = 0; j < around; j++) {
      const b = (j / around) * TAU;
      const cb = Math.cos(b) * r, sb = Math.sin(b) * r;
      idx.push(verts.length);
      verts.push([cx + nx * cb, cy + ny * cb, sb]);
    }
    rings.push(idx);
  }
  for (let i = 0; i < along; i++) band(faces, rings[i], rings[i + 1]);
  faces.push(rings[0].slice().reverse());
  faces.push(rings[along].slice());
  return { verts, faces };
};

/** Extrude a flat outline (points in the x/y plane) straight back along z. */
const extrude = (profile, depth) => {
  let area = 0;
  for (let i = 0; i < profile.length; i++) {
    const [ax, ay] = profile[i], [bx, by] = profile[(i + 1) % profile.length];
    area += ax * by - bx * ay;
  }
  const pts = area < 0 ? profile.slice().reverse() : profile;   // make it counter-clockwise
  const n = pts.length, hz = depth / 2;
  const verts = [];
  for (const [x, y] of pts) verts.push([x, y, hz]);
  for (const [x, y] of pts) verts.push([x, y, -hz]);
  const faces = [pts.map((_, i) => i), pts.map((_, i) => 2 * n - 1 - i)];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    faces.push([i, n + i, n + j, j]);
  }
  return { verts, faces };
};

const arrowMesh = () => {
  const shaft = 0.21, neck = 0.5 - 0.38;      // half shaft height, where the head begins
  return extrude([
    [-0.5, -shaft], [neck, -shaft], [neck, -0.5], [0.5, 0],
    [neck, 0.5], [neck, shaft], [-0.5, shaft],
  ], 0.34);
};

// A rod lying along X: the capsule turned on its side, built directly so the caps stay round.
const rodMesh = (slices = 20, capStacks = 6) => {
  const verts = [], faces = [];
  const r = 0.24;
  const xBody = 0.5 - r;
  const ringAtX = (cx, rr) => {
    const idx = [];
    for (let i = 0; i < slices; i++) {
      const a = (i / slices) * TAU;
      idx.push(verts.length);
      verts.push([cx, Math.sin(a) * rr, Math.cos(a) * rr]);
    }
    return idx;
  };
  const rings = [];
  for (let s = 0; s <= capStacks; s++) {            // left cap
    const a = (Math.PI / 2) * (1 - s / capStacks);
    rings.push(ringAtX(-xBody - Math.sin(a) * r, Math.cos(a) * r));
  }
  for (let s = 0; s <= capStacks; s++) {            // right cap
    const a = (Math.PI / 2) * (s / capStacks);
    rings.push(ringAtX(xBody + Math.sin(a) * r, Math.cos(a) * r));
  }
  // Swapped, for the same reason as the ring: it puts this tube's faces outward.
  for (let s = 0; s < rings.length - 1; s++) band(faces, rings[s + 1], rings[s]);
  return { verts, faces };
};

/**
 * Every 3D figure's geometry, built once and reused.
 *
 * The plain sphere is deliberately NOT here. A ball looks the same from every direction, so
 * real geometry buys it nothing and costs it something: a few hundred flat facets whose
 * shading steps are visible as bands. It keeps the smooth shaded circle it always had (see the
 * no-mesh fallback in renderShapeElement), which is both better looking and far cheaper. Every
 * shape whose appearance DOES depend on which way it faces has a mesh.
 */
const BUILDERS = {
  cube: cubeMesh,
  hemisphere: hemisphereMesh,
  cone: coneMesh,
  pyramid: pyramidMesh,
  cylinder: cylinderMesh,
  capsule: capsuleMesh,
  torus: torusMesh,
  pipe: pipeMesh,
  arrow3d: arrowMesh,
  rod3d: rodMesh,
};

const _meshCache = new Map();
export const mesh3D = (type) => {
  if (!_meshCache.has(type)) {
    const build = BUILDERS[type];
    _meshCache.set(type, build ? build() : null);
  }
  return _meshCache.get(type);
};

export const has3DMesh = (type) => !!BUILDERS[type];

/**
 * The angles a 3D object sits at before anyone turns it.
 *
 * Straight-on (0, 0) is geometrically correct and visually useless -- a cube seen exactly
 * face-on is a square, a cylinder is a rectangle. The old painted figures faked a
 * three-quarter view, so that is where a new object starts, and where every object drawn
 * before there were angles to store starts too.
 */
export const DEFAULT_3D_POSE = { rx: 20, ry: -32 };

/**
 * ...except that this default only suits a solid whose long axis is UP.
 *
 * Most of these meshes are built around Y (cube, cone, cylinder, capsule, pyramid, hemisphere,
 * torus), and a -32° spin shows them off nicely. Three are not: the arrow and the elbow pipe are
 * flat profiles lying in the XY plane with a little depth in Z, and the rod runs along X. Spinning
 * those by the same -32° turns them AWAY from the viewer instead of around their own height, which
 * foreshortens the very axis you are meant to read and leaves them looking skewed rather than
 * placed -- an arrow that appears to be twisting out of the page. They get a gentler pose: enough
 * angle to show thickness and a cap, not enough to throw their length away.
 */
const POSE_BY_TYPE = {
  arrow3d: { rx: 10, ry: -16 },   // flat profile in XY -- read it nearly face-on
  pipe:    { rx: 12, ry: -18 },   // elbow, same plane, same reasoning
  rod3d:   { rx: 18, ry: -12 },   // long axis is X: tilt to see along it, barely spin
};

/** A figure's three angles, with its own type's default view filled in where it has none. */
export const pose3D = (shape) => {
  const d = (shape && POSE_BY_TYPE[shape.type]) || DEFAULT_3D_POSE;
  return {
    rx: shape && shape.rotX != null ? shape.rotX : d.rx,
    ry: shape && shape.rotY != null ? shape.rotY : d.ry,
    rz: (shape && shape.rotation) || 0,
  };
};

/**
 * The ball's seams.
 *
 * A plain ball looks the same from every direction, so turning one with the orbit handle changed
 * nothing anybody could see, and the handle looked broken. Seams give it something to turn: three
 * great circles at right angles, like the lines on a beach ball, turned in the same spin-then-
 * tilt order as every mesh (see project3D), with only the half facing the viewer drawn. The body
 * stays the smooth shaded circle (see the note on BUILDERS) — only the lines move.
 *
 * Returns polylines [[x, y], ...] in the figure's own w x h box. The in-plane turn (rz) is not in
 * here: the ball's body is turned by its caller's rotation, and the seams ride along with it.
 */
export const sphereSeams3D = (w, h, angles, segments = 72) => {
  const rx = ((angles && angles.rx) || 0) * Math.PI / 180;
  const ry = ((angles && angles.ry) || 0) * Math.PI / 180;
  const cxr = Math.cos(rx), sxr = Math.sin(rx), cyr = Math.cos(ry), syr = Math.sin(ry);
  const R = 0.5;
  const circles = [
    (a) => [R * Math.cos(a), 0, R * Math.sin(a)],   // the equator
    (a) => [0, R * Math.cos(a), R * Math.sin(a)],   // over the top, front to back
    (a) => [R * Math.cos(a), R * Math.sin(a), 0],   // over the top, side to side
  ];
  const runs = [];
  for (const at of circles) {
    let run = [];
    for (let i = 0; i <= segments; i++) {
      const [x, y, z] = at((i / segments) * TAU);
      const x1 = x * cyr + z * syr, z1 = -x * syr + z * cyr;
      const y2 = y * cxr - z1 * sxr, z2 = y * sxr + z1 * cxr;
      if (z2 >= 0) run.push([round(w / 2 + x1 * w), round(h / 2 - y2 * h)]);
      else if (run.length) { if (run.length > 1) runs.push(run); run = []; }
    }
    if (run.length > 1) runs.push(run);
  }
  return runs;
};

/* ------------------------------- projection ------------------------------- */

// Straight down the Z axis: no perspective, so what is drawn scales exactly and the canvas
// exporter can reproduce it without any camera maths of its own.
const LIGHT = (() => {
  const v = [-0.35, 0.58, 0.74];
  const n = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / n, v[1] / n, v[2] / n];
})();

const AMBIENT = 0.32;

const _projCache = new Map();
const round = (n, k = 100) => Math.round(n * k) / k;

/**
 * Turn a 3D figure into flat, shaded, back-to-front polygons.
 *
 * type    one of the SHAPES_3D keys
 * w, h    the figure's box on the sheet, in the destination's own pixels
 * angles  { rx, ry, rz } in degrees -- rx tilts forward/back, ry spins left/right,
 *         rz is the in-plane turn the rotate handle has always done
 * colour  the body colour (see bodyColour3D)
 *
 * Returns [{ pts: [[x, y], ...], fill }] already ordered so painting them in sequence gives
 * the right result, with faces pointing away from the viewer dropped.
 */
export const project3D = (type, w, h, angles, colour) => {
  const m = mesh3D(type);
  if (!m) return [];
  const rx = ((angles && angles.rx) || 0) * Math.PI / 180;
  const ry = ((angles && angles.ry) || 0) * Math.PI / 180;
  const rz = ((angles && angles.rz) || 0) * Math.PI / 180;

  const key = `${type}|${round(w, 10)}|${round(h, 10)}|${round(rx, 1000)}|${round(ry, 1000)}|${round(rz, 1000)}|${colour}`;
  const hit = _projCache.get(key);
  if (hit) return hit;

  const cxr = Math.cos(rx), sxr = Math.sin(rx);
  const cyr = Math.cos(ry), syr = Math.sin(ry);
  const czr = Math.cos(rz), szr = Math.sin(rz);

  // Rotate every vertex once: Y (spin) FIRST, then X (tilt), then Z (the flat in-plane turn).
  //
  // The order matters and only one of them looks right. Tilting first leaves the object's own
  // upright axis lying at an angle, and the spin -- which turns about the WORLD's vertical --
  // then swings that tilted axis sideways, so a cylinder or a cube ends up leaning over like
  // it is about to fall. Spinning first turns the object about its own upright axis (where an
  // upright thing stays upright), and the tilt afterwards just raises the viewpoint, the way
  // stepping up a little to look down on something does. Uprights stay upright, and you still
  // see the top.
  const rot = m.verts.map(([x, y, z]) => {
    const x1 = x * cyr + z * syr, z1 = -x * syr + z * cyr;
    const y2 = y * cxr - z1 * sxr, z2 = y * sxr + z1 * cxr;
    const x3 = x1 * czr - y2 * szr, y3 = x1 * szr + y2 * czr;
    return [x3, y3, z2];
  });

  const out = [];
  for (const f of m.faces) {
    const a = rot[f[0]], b = rot[f[1]], c = rot[f[2]];
    // Face normal from two edges. The winding is outward-CCW, so a normal leaning towards the
    // viewer (positive z) means we are looking at the outside of that face.
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const nl = Math.hypot(nx, ny, nz);
    if (!nl) continue;
    nx /= nl; ny /= nl; nz /= nl;
    // Every mesh is wound outward (checked by signed volume), so a normal leaning away from
    // the viewer means we are looking at the back of that face: skip it.
    if (nz <= 0.0001) continue;
    const lam = Math.max(0, nx * LIGHT[0] + ny * LIGHT[1] + nz * LIGHT[2]);
    const t = AMBIENT + (1 - AMBIENT) * lam;          // 0.32 .. 1
    const fill = shade(colour, -0.34 + ((t - AMBIENT) / (1 - AMBIENT)) * 0.70);
    let depth = 0;
    const pts = [];
    for (const i of f) {
      const v = rot[i];
      depth += v[2];
      // Y flips here, and here only: maths has Y up, the sheet has Y down.
      pts.push([round(w / 2 + v[0] * w), round(h / 2 - v[1] * h)]);
    }
    out.push({ pts, fill, depth: depth / f.length });
  }
  out.sort((p, q) => p.depth - q.depth);              // furthest first
  for (const f of out) delete f.depth;

  // ── Fit the figure to the box it was given ──────────────────────────────────────────────────
  //
  // A mesh turned in space reaches past the face it was sized by: a cube tipped towards you is
  // about 1.4 times as wide as its own front. That box is the one the person dragged out, the one
  // the selection handles draw, and the one the W and H boxes report — so a figure that overflowed
  // it made all three lie at once. The handles sat inside the cube, the numbers described
  // something smaller than what was on the sheet, and saving the figure on its own had to choose
  // between cropping the corners off and declaring a size nobody had asked for.
  //
  // Fitting the projection to the box ends all of that: the figure occupies exactly the space it
  // was given, whatever angle it is turned to. The cost is that a turn changes the figure's scale
  // slightly, since a cube seen corner-on has to shrink to fit the same width — which is the right
  // trade, because the alternative is an object whose stated size is not its size.
  let fx0 = Infinity, fy0 = Infinity, fx1 = -Infinity, fy1 = -Infinity;
  for (const f of out) {
    for (const [x, y] of f.pts) {
      if (x < fx0) fx0 = x; if (x > fx1) fx1 = x;
      if (y < fy0) fy0 = y; if (y > fy1) fy1 = y;
    }
  }
  const spanX = fx1 - fx0, spanY = fy1 - fy0;
  if (spanX > 0 && spanY > 0) {
    // Each axis is fitted on its own, the same way the mesh was stretched to w x h in the first
    // place — drag out a wide box and you still get a wide figure.
    const sx = w / spanX, sy = h / spanY;
    for (const f of out) {
      for (const p of f.pts) {
        p[0] = round((p[0] - fx0) * sx);
        p[1] = round((p[1] - fy0) * sy);
      }
    }
  }

  if (_projCache.size > 400) _projCache.clear();
  _projCache.set(key, out);
  return out;
};

/** The same faces as an SVG path "d", for consumers that want one string per face. */
export const facePath = (pts) => 'M' + pts.map(([x, y]) => `${x},${y}`).join('L') + 'Z';

const _boundsCache = new Map();
/**
 * How far a projected figure reaches outside the w x h box it is drawn in, as fractions of
 * that box: { x0, y0, x1, y1 }, where 0..1 is the box itself and x0 < 0 or x1 > 1 mean the
 * figure spills past that edge.
 *
 * A 3D figure is a mesh turned in space and then flattened, so a cube tipped towards you
 * reaches past its own front face — and the front face is the box that was dragged out for it.
 * Anything that crops a figure to that box (saving one on its own, for instance) has to know
 * by how much, or it slices the corners off.
 *
 * The projection scales linearly with w and h, so the fractions depend only on the type and
 * the angles: one measurement at a nominal size answers for every size.
 */
export const bounds3D = (type, angles) => {
  const a = angles || {};
  const key = `${type}|${Math.round(a.rx || 0)}|${Math.round(a.ry || 0)}|${Math.round(a.rz || 0)}`;
  const hit = _boundsCache.get(key);
  if (hit) return hit;
  const N = 1000;                                   // big enough that project3D's rounding is noise
  const faces = project3D(type, N, N, a, '#808080');
  let x0 = 0, y0 = 0, x1 = N, y1 = N;
  for (const f of faces) {
    for (const [x, y] of f.pts) {
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  const out = { x0: x0 / N, y0: y0 / N, x1: x1 / N, y1: y1 / N };
  if (_boundsCache.size > 400) _boundsCache.clear();
  _boundsCache.set(key, out);
  return out;
};
